import { useRef, useState } from 'react';
import { Paper, Grid, IconButton, useMediaQuery } from '@mui/material';
import { findIndex } from 'lodash';
import { ChevronLeft, ChevronRight } from '../Icons';
import { DEFAULT_IMAGE } from '../../utils/transformers';

import Styles from './Gallery.module.scss';
import CloudflareResizedImage from '../CloudflareResizedImage';

interface ImageProps {
  alt: unknown | null;
  id: number;
  large: string;
  medium: string;
  small: string;
  src: string;
}

interface VideoProps {
  src: string;
}

type Media = ImageProps | VideoProps;

export interface GalleryProps {
  items?: Media[];
  name?: string;
}

export const Gallery: React.FC<GalleryProps> = ({ items, name }) => {
  const gridRef = useRef<HTMLDivElement>(null);

  const images =
    items?.map((img) => ({
      thumbnail: img.src,
      src: img.src,
    })) || [];

  const [imageSrc, setImageSrc] = useState(items?.[0]?.src || DEFAULT_IMAGE);

  const isGreaterThanTablet = useMediaQuery('(min-width:768px)');

  const navigateImage = (index: number) => {
    if (index === images.length) {
      setImageSrc(images[0].src);
      return;
    }

    if (index === -1) {
      setImageSrc(images[images.length - 1].src);
      return;
    }

    setImageSrc(images[index].src);
  };

  return (
    <div className={Styles.gallery} data-testid="gallery">
      <div className={Styles.mainImageWrap}>
        <CloudflareResizedImage
          className={Styles.mainImage}
          layout="fill"
          objectFit="cover"
          objectPosition="center"
          alt={name || ''}
          src={imageSrc}
          loading="lazy"
          sizes={`(max-width: 767px) 100vw,
            ((min-width: 768px) and (max-width: 1023px)) calc(100vw - 80px),
            ((min-width: 1024px) and (max-width: 1201px)) calc(100vw - 160px),
            ((min-width: 1202px) and (max-width: 1599px)) calc(1201px - 160px),
            (min-width: 1600px) calc(1201px - 40px)`}
        />
        {images.length > 1 && (
          <>
            <div className={Styles.mainImagePreviousWrap} style={{ left: '20px' }}>
              <IconButton
                aria-label="previous image"
                className={Styles.mainImagePrevious}
                type="button"
                onClick={() =>
                  navigateImage(images.map((image) => image.src).indexOf(imageSrc) - 1)
                }
                data-testid="gallery-previous"
              >
                <ChevronLeft />
              </IconButton>
            </div>
            <div className={Styles.mainImageNextWrap} style={{ right: '20px' }}>
              <IconButton
                aria-label="next image"
                className={Styles.mainImageNext}
                type="button"
                onClick={() =>
                  navigateImage(images.map((image) => image.src).indexOf(imageSrc) + 1)
                }
                data-testid="gallery-next"
              >
                <ChevronRight />
              </IconButton>
            </div>
          </>
        )}
      </div>
      {images.length > 1 && (
        <Grid
          ref={gridRef}
          style={{ margin: '0 0 0 -4px', width: '100%' }}
          container
          justifyContent="center"
          spacing={2}
          data-testid="gallery-thumbnails"
        >
          <Grid style={{ padding: '0 0 0 12px' }} item mobile={12}>
            <Grid
              className={Styles.imageGridInner}
              justifyContent={isGreaterThanTablet ? 'center' : 'left'}
              container
              spacing={1}
            >
              {images.map((image, index) => (
                <Grid key={`image ${index + 1}`} item>
                  <Paper
                    className={`${Styles.gridImageCard} ${
                      index === findIndex(images, { src: imageSrc }) ? Styles.activeImage : ''
                    }`}
                  >
                    <div className={Styles.gridImageCardInner}>
                      <CloudflareResizedImage
                        lazyRoot={gridRef}
                        loading="lazy"
                        height={66}
                        width={93}
                        sizes="(min-width: 350px) 93px, 71px"
                        layout="intrinsic"
                        objectFit="cover"
                        objectPosition="center"
                        alt={name || ''}
                        className={Styles.gridImage}
                        src={image.thumbnail}
                        onClick={() => {
                          setImageSrc(image.src);
                        }}
                      />
                    </div>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default Gallery;
